import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

export const CODE_SECURITY_PATH = "/admin/code-security";

export const SCOPE_TOGGLE_SX = {
  borderRadius: "26px",
  border: "1px solid #E8EAED",
  gap: 0.2,
  overflow: "hidden",
  boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
  "& .MuiToggleButton-root": {
    border: "none",
    borderRadius: "36px",
    color: "#6B7280",
    textTransform: "none",
    fontWeight: 600,
    fontSize: "0.75rem",
    px: 3,
    py: 1,
    minWidth: "70px",
    transition: "all 0.2s ease-in-out",
    "&.Mui-selected": {
      backgroundColor: "#3B82F6",
      color: "#fff",
      boxShadow: "inset 0 1px 2px 0 rgba(0, 0, 0, 0.05)",
      "&:hover": { backgroundColor: "#2563EB" },
    },
    "&:hover:not(.Mui-selected)": {
      backgroundColor: "#F9FAFB",
      color: "#374151",
    },
  },
};

export const TABLE_MIN_WIDTH_SX = {
  "& table": {
    minWidth: "unset !important",
  },
};

export const FILTER_BAR_WRAP_SX = {
  p: 2,
  pb: 1.5,
  backgroundColor: "#FFFFFF",
};

export const matchesFilter = (selected, value) =>
  !selected || selected.length === 0 || selected.includes(value);

export const SECRETS_FOUND_FILTER_OPTIONS = [
  { id: "yes", label: "Yes" },
  { id: "no", label: "No" },
];

export const secretsFoundValue = (levelCounts, scoredLevels) => {
  const hasSecrets = (levelCounts || []).some(
    (lc) => scoredLevels.includes(lc.level) && Number(lc.count) > 0
  );
  return hasSecrets ? "yes" : "no";
};

export const hasActiveFilters = (searchQuery, tableFilters) =>
  Boolean(searchQuery) ||
  Object.values(tableFilters || {}).some(
    (selected) => Array.isArray(selected) && selected.length > 0
  );

export const STATUS_SORT = { fail: 0, pending: 1, pass: 2 };

export const compareStatusFailingFirst = (a, b) => {
  const rankA = STATUS_SORT[String(a.status || "").toLowerCase()] ?? 1;
  const rankB = STATUS_SORT[String(b.status || "").toLowerCase()] ?? 1;
  return rankA - rankB;
};

const LIST_FILTER_KEYS = ["status", "findings"];

const csvValues = (value) =>
  String(value || "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);

export const parseListFilters = (searchParams) => {
  const tab = searchParams.get("tab") === "out" ? "out" : "in";
  const searchQuery = searchParams.get("q") || "";
  const tableFilters = {};
  LIST_FILTER_KEYS.forEach((key) => {
    const values = csvValues(searchParams.get(key));
    if (values.length) tableFilters[key] = values;
  });
  return { tab, searchQuery, tableFilters };
};

export const serializeListFilters = ({ tab, searchQuery, tableFilters }) => {
  const params = new URLSearchParams();
  if (tab && tab !== "in") params.set("tab", tab);
  const query = String(searchQuery || "").trim();
  if (query) params.set("q", query);
  LIST_FILTER_KEYS.forEach((key) => {
    const values = tableFilters?.[key];
    if (Array.isArray(values) && values.length > 0) {
      params.set(key, values.join(","));
    }
  });
  return params;
};

export const useCodeSecurityListFilters = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const searchKey = searchParams.toString();
  const parsed = useMemo(
    () => parseListFilters(new URLSearchParams(searchKey)),
    [searchKey]
  );

  const write = useCallback(
    (next) => {
      const nextStr = serializeListFilters(next).toString();
      if (nextStr === searchKey) return;
      setSearchParams(serializeListFilters(next), { replace: true });
    },
    [searchKey, setSearchParams]
  );

  const setTab = useCallback(
    (tab) => {
      write({ tab, searchQuery: "", tableFilters: {} });
    },
    [write]
  );

  const setSearchQuery = useCallback(
    (searchQuery) => {
      write({
        tab: parsed.tab,
        searchQuery,
        tableFilters: parsed.tableFilters,
      });
    },
    [parsed.tab, parsed.tableFilters, write]
  );

  const setTableFilters = useCallback(
    (tableFilters) => {
      write({
        tab: parsed.tab,
        searchQuery: parsed.searchQuery,
        tableFilters,
      });
    },
    [parsed.searchQuery, parsed.tab, write]
  );

  const clearFilters = useCallback(() => {
    write({ tab: parsed.tab, searchQuery: "", tableFilters: {} });
  }, [parsed.tab, write]);

  return {
    tab: parsed.tab,
    searchQuery: parsed.searchQuery,
    tableFilters: parsed.tableFilters,
    setTab,
    setSearchQuery,
    setTableFilters,
    clearFilters,
  };
};

export const includesQuery = (query, ...parts) => {
  if (!query) return true;
  const needle = String(query).toLowerCase();
  return parts.some((part) =>
    String(part || "")
      .toLowerCase()
      .includes(needle)
  );
};

export const countLabel = (count, singular, plural = `${singular}s`) =>
  `${count} ${count === 1 ? singular : plural} found`;

export const CARD_HEADER_SX = {
  borderBottom: "1px solid #ddd",
  background: "#F7F7FC",
  padding: "12px 14px",
  "& .MuiCardHeader-action": {
    m: 0,
    display: "flex",
    alignItems: "center",
  },
};

export const primaryButtonSx = (theme) => ({
  bgcolor: theme.customColors.button.primary,
  color: theme.customColors.button.text,
  fontWeight: 500,
  borderRadius: "8px",
  textTransform: "none",
  fontSize: "14px",
  px: 2,
  py: 1,
  "&:hover": {
    bgcolor: theme.palette._blue?.dark || theme.customColors.button.primary,
  },
  "&:disabled": {
    bgcolor: theme.customColors.background.light,
    color: theme.customColors.text.disabled,
  },
});

export const outlinedButtonSx = (theme) => ({
  fontWeight: 500,
  borderRadius: "8px",
  textTransform: "none",
  fontSize: "14px",
  px: 2,
  py: 1,
  borderColor: theme.customColors.border.main,
  color: theme.customColors.text.strong,
  "&:hover": {
    borderColor: theme.customColors.border.strong,
    backgroundColor: theme.customColors.background.hover,
  },
  "&:disabled": {
    borderColor: theme.customColors.border.disabled,
    color: theme.customColors.text.disabled,
  },
});

